import { useCardRollover } from './useCardRollover';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePassportArtwork } from './usePassportArtwork';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import { cardSubjectPath, type PassportCard as Card } from '../../services/api/passportCards';
import { cardEditionLabel } from '@bilt/domain/cardRecord';
import PassportVisualLayers, { visualMaterialStyle } from './PassportVisualLayers';
import PassportMaterial from './PassportMaterial';
import PassportTypeFront from './PassportTypeFront';
import './passport-types.css';
import PassportRecordBack from './PassportRecordBack';
import './passport-cabinet.css';
import CollectorVehicleDetails from './CollectorVehicleDetails';
import './collector-template.css';
import { usePassportKindVisual } from './usePassportKindVisual';
import CollectorActionControls, { type CollectorActions } from './CollectorActions';
import CtaIcon from '../ui/CtaIcon';
import { createPortal } from 'react-dom';
import Modal from '../ui/Modal';
function CardAsset({
  id,
  className,
  materialMask = false,
}: {
  id?: string;
  className: string;
  materialMask?: boolean;
}) {
  const url = usePassportArtwork(id);
  return url ? (
    materialMask ? (
      <span
        className="pe-mask-asset"
        style={{ maskImage: `url(${url})`, WebkitMaskImage: `url(${url})` }}
        aria-hidden="true"
      />
    ) : (
      <img className={className} src={url} alt="" />
    )
  ) : null;
}
export default function PassportCard({
  card: inputCard,
  staticOutput = false,
  onOwner,
  onRecognition,
  demo = false,
  back: controlledBack,
  quiet = false,
  reduceMotion = false,
  imageAlt = '',
  loadBuildDetails = true,
  readableStory = false,
  backLabel = 'Card story',
  fullRecordExtension,
  actions,
  frontArtAction,
  onFlip: suppliedFlip,
}: {
  card: Card;
  /** Exact bounded face without active controls, for print and decorative snapshots. */
  staticOutput?: boolean;
  onOwner?: () => void;
  onRecognition?: () => void;
  demo?: boolean;
  back?: boolean;
  /** Suppress passive material effects, not a deliberate turn. */
  quiet?: boolean;
  reduceMotion?: boolean;
  imageAlt?: string;
  loadBuildDetails?: boolean;
  readableStory?: boolean;
  backLabel?: string;
  /** Live listing details and actions, physically contained by the rotating front. */
  fullRecordExtension?: React.ReactNode;
  actions?: CollectorActions;
  frontArtAction?: React.ReactNode;
  onFlip?: () => void;
}) {
  const visual = usePassportKindVisual(
    !inputCard.id && !inputCard.design.visual && inputCard.subject_id > 0
      ? inputCard.subject_type
      : null
  );
  const card =
    inputCard.design.visual || !visual
      ? inputCard
      : { ...inputCard, design: { ...inputCard.design, visual } };
  const [localBack, setLocalBack] = useState(false);
  const back = controlledBack ?? localBack;
  const onFlip =
    suppliedFlip ||
    (controlledBack === undefined || staticOutput ? () => setLocalBack((v) => !v) : undefined);
  const sourcePath = cardSubjectPath(card);
  const selectedActions = {
    ...actions,
    vehicle:
      actions?.vehicle ||
      (!demo && !staticOutput && card.subject_type === 'vehicle' && card.subject_id > 0
        ? {
            id: card.subject_id,
            like: {
              count: Number(card.subject?.facts?.like_count ?? 0),
              selected: Number(card.subject?.facts?.user_has_liked) === 1,
            },
            favorite: {
              count: Number(card.subject?.facts?.favorite_count ?? 0),
              selected: Number(card.subject?.facts?.is_favorited) === 1,
            },
          }
        : undefined),
    source:
      actions?.source ||
      (!demo && sourcePath
        ? {
            href: sourcePath,
            label: `View ${card.subject_type === 'vehicle' ? 'build' : card.subject_type}`,
          }
        : undefined),
  };
  const [recordOpen, setRecordOpen] = useState(false);
  const [backMounted, setBackMounted] = useState(back);
  useEffect(() => {
    if (back) setBackMounted(true);
  }, [back]);
  const turnRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<HTMLElement>(null);
  const rollover = useCardRollover(sceneRef, staticOutput || reduceMotion);
  const interactionLight = !staticOutput && (
    <span className="ct-card-light" aria-hidden="true">
      <span className="ct-card-sweep" />
      <span className="ct-card-rim" />
    </span>
  );
  const animation = useRef<Animation | null>(null);
  const previousSide = useRef(back);
  const restoreFlipFocus = useRef(false);
  useLayoutEffect(() => {
    if (restoreFlipFocus.current) {
      sceneRef.current
        ?.querySelector<HTMLButtonElement>(`[data-card-flip="${back ? 'back' : 'front'}"]`)
        ?.focus({ preventScroll: true });
      restoreFlipFocus.current = false;
    }
  }, [back]);
  const flipControl = (face: 'front' | 'back') =>
    onFlip ? (
      <button
        type="button"
        className="pc-card-flip"
        data-card-flip={face}
        aria-label={`Turn ${card.title} ${face === 'back' ? 'to front' : 'over'}`}
        aria-pressed={face === 'back'}
        tabIndex={(face === 'back') === back ? 0 : -1}
        title={face === 'back' ? 'Show front' : 'Turn card over'}
        onClick={(event) => {
          event.stopPropagation();
          restoreFlipFocus.current = document.activeElement === event.currentTarget;
          onFlip();
        }}
      >
        <CtaIcon direction="turn" compact />
      </button>
    ) : null;
  useLayoutEffect(() => {
    const turn = turnRef.current;
    const scene = sceneRef.current;
    if (!turn || !scene || typeof window.matchMedia !== 'function') return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!reduced) return;
    const target = `rotateY(${back ? 180 : 0}deg)`;
    const settle = () => {
      animation.current?.cancel();
      animation.current = null;
      turn.style.transform = target;
      scene.dataset.phase = 'settled';
    };
    if (previousSide.current !== back && !reduceMotion && !reduced.matches && turn.animate) {
      // The new face class has already committed. Read an interrupted animation,
      // otherwise begin at the previous face rather than its new CSS endpoint.
      const current = animation.current
        ? getComputedStyle(turn).transform
        : `rotateY(${previousSide.current ? 180 : 0}deg)`;
      animation.current?.cancel();
      turn.style.transform = target;
      scene.dataset.phase = 'turning';
      const motion = turn.animate(
        [
          { transform: current, offset: 0 },
          { transform: current, offset: 0.12 },
          { transform: target, offset: 1 },
        ],
        { duration: 820, easing: 'cubic-bezier(.4,0,.16,1)' }
      );
      animation.current = motion;
      void motion.finished
        .then(() => {
          if (animation.current === motion) settle();
        })
        .catch(() => undefined);
    } else settle();
    previousSide.current = back;
    reduced.addEventListener('change', settle);
    return () => {
      reduced.removeEventListener('change', settle);
    };
  }, [back, reduceMotion]);
  useEffect(
    () => () => {
      animation.current?.cancel();
    },
    []
  );
  const art = usePassportArtwork(card.design.art_id);
  const collectible = card.subject?.collectible;
  const image = card.design.art_id
    ? art
    : resolvePublicFileUrl(
        collectible?.cover_url || card.subject?.image_url || card.subject?.thumbnail_url
      );
  const logoOnly = !card.design.art_id && !collectible?.cover_url && !!collectible?.logo_url;
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const surface = `pc-card pc-template-${card.design.template} pc-finish-${card.design.finish} pc-accent-${card.design.accent} pc-layout-${card.design.layout}`;
  const edition = !demo && cardEditionLabel(card.ordinal, card.rules.cap);
  return (
    <>
      <article
        ref={sceneRef}
        onPointerEnter={(event) => {
          if (event.pointerType !== 'touch') rollover();
        }}
        onPointerDown={rollover}
        onFocus={rollover}
        data-reduced-motion={reduceMotion || undefined}
        inert={staticOutput}
        data-static={staticOutput || undefined}
        className={`pc-card-scene ${card.subject_type === 'vehicle' ? 'pc-card-with-details' : ''} ct-card-scene ${onFlip ? 'pc-card-has-controls' : ''}`}
        style={visualMaterialStyle(card.design.visual)}
        data-template-version="bilt.collector.v2"
        data-kind={card.subject_type}
        data-side={back ? 'back' : 'front'}
        data-quiet={quiet || undefined}
      >
        <div ref={turnRef} className={`pc-card-turn ${back ? 'is-turned' : ''}`}>
          <div className={`${surface} pc-card-face pc-card-front`} aria-hidden={back} inert={back}>
            <div className="pc-card-front-art">
              {frontArtAction}
              <PassportTypeFront
                card={card}
                marks={
                  <CollectorVehicleDetails
                    section="marks"
                    card={card}
                    onRecognition={onRecognition || (() => setRecordOpen(true))}
                  />
                }
                logo={
                  card.design.logo_art_id ? (
                    <CardAsset id={card.design.logo_art_id} className="pc-issuer-logo" />
                  ) : collectible?.logo_url ? (
                    <img
                      src={resolvePublicFileUrl(collectible.logo_url) || undefined}
                      className="pc-issuer-logo"
                      alt=""
                    />
                  ) : null
                }
                image={
                  image && image !== failedImage ? (
                    <img
                      className={logoOnly ? 'pc-card-image-logo' : undefined}
                      src={image}
                      alt={imageAlt}
                      width="600"
                      height="650"
                      loading="lazy"
                      onError={() => setFailedImage(image)}
                      style={{
                        objectPosition:
                          !card.id && !card.design.art_id
                            ? `${Math.min(1, Math.max(0, card.subject?.focal_x ?? 0.5)) * 100}% ${Math.min(1, Math.max(0, card.subject?.focal_y ?? card.design.crop / 100)) * 100}%`
                            : `50% ${card.design.crop}%`,
                        objectFit: logoOnly ? 'contain' : 'cover',
                      }}
                    />
                  ) : (
                    <div className="pc-card-monogram" aria-hidden="true">
                      B<span>:</span>
                    </div>
                  )
                }
              />
            </div>
            <PassportVisualLayers
              card={card}
              face="front"
              asset={(id, mask) => (
                <CardAsset id={id} className="pe-layer-image" materialMask={mask} />
              )}
            />
            <PassportMaterial visual={card.design.visual} quiet={quiet || back} face="front" />
            <CollectorVehicleDetails
              card={card}
              onOwner={onOwner}
              onRecognition={onRecognition || (() => setRecordOpen(true))}
            />
            {(onFlip || selectedActions) && (
              <div className="pc-card-controls">
                <div className="pc-card-actions">
                  <CollectorActionControls actions={selectedActions} />
                </div>
                {flipControl('front')}
              </div>
            )}
            <span className="pc-card-sheen" aria-hidden="true" />
            {interactionLight}
          </div>
          <div className={`${surface} pc-card-face pc-card-back`} aria-hidden={!back} inert={!back}>
            {(back || backMounted) && (
              <>
                {edition && <span className="pe-kicker">{edition}</span>}
                <div
                  className="pc-card-custom-back"
                  tabIndex={back && readableStory ? 0 : undefined}
                  role={readableStory ? 'region' : undefined}
                  aria-label={readableStory ? backLabel : undefined}
                >
                  {(back || backMounted) && (
                    <PassportRecordBack
                      card={card}
                      demo={demo}
                      active={back}
                      loadBuildDetails={false}
                      compact
                    />
                  )}
                </div>
                {(back || backMounted) && (
                  <PassportVisualLayers
                    card={card}
                    face="back"
                    asset={(id, mask) => (
                      <CardAsset id={id} className="pe-layer-image" materialMask={mask} />
                    )}
                  />
                )}
                <PassportMaterial visual={card.design.visual} quiet={quiet || !back} face="back" />
                <span className="pc-card-sheen" aria-hidden="true" />
                {interactionLight}
              </>
            )}
            {(onFlip || readableStory) && (
              <div className="pc-card-controls">
                <div className="pc-card-actions">
                  <CollectorActionControls
                    actions={
                      selectedActions?.source ? { source: selectedActions.source } : undefined
                    }
                  />
                  {edition && (
                    <button
                      type="button"
                      className="pc-edition-record"
                      onClick={() => setRecordOpen(true)}
                      aria-label={`Edition details: ${edition}`}
                    >
                      Edition details
                    </button>
                  )}
                </div>
                {flipControl('back')}
              </div>
            )}
          </div>
        </div>
      </article>
      {recordOpen &&
        createPortal(
          <Modal
            isOpen
            onClose={() => setRecordOpen(false)}
            title={`${card.title} · Full record`}
            size="lg"
            initialFocus="dialog"
            className="pc-full-record"
            footer={
              <button className="pc-button secondary" onClick={() => setRecordOpen(false)}>
                Back to card
              </button>
            }
          >
            {fullRecordExtension}
            {
              <PassportRecordBack
                card={card}
                demo={demo}
                active
                loadBuildDetails={loadBuildDetails}
              />
            }
            {card.design.back_art_id && (
              <CardAsset id={card.design.back_art_id} className="pc-record-art-image" />
            )}
          </Modal>,
          document.body
        )}
    </>
  );
}
